
import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Input, Row, Col, Modal, Form, Select, Radio, DatePicker, message } from 'antd';
import { EditOutlined, DeleteOutlined, SearchOutlined, PlusOutlined, PrinterOutlined } from '@ant-design/icons';
import { RiFolderTransferFill } from "react-icons/ri";
import moment from 'moment';
import api from '../api';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import SiteLogo from '../assests/logo.png'; // Ensure this path is correct

const { RangePicker } = DatePicker;

const Transactions = () => {
  const [searchText, setSearchText] = useState('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentRecord, setCurrentRecord] = useState(null);
  const [form] = Form.useForm();
  const [data, setData] = useState([]);
  const [memberOptions, setMemberOptions] = useState([]);
  const [eventOptions, setEventOptions] = useState([]);
  const [selectedFilterEvent, setSelectedFilterEvent] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([fetchData(), fetchDataMember(), fetchDataEvent()])
      .then(() => setLoading(false))
      .catch(() => setLoading(false));
  }, []);

  const fetchData = async () => {
    try {
      const response = await api.get('/transaction/allTransaction');
      if (response.data && Array.isArray(response.data.data)) {
        setData(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch transaction data:', error);
      message.error('Failed to fetch transaction data. Please try again later.');
    }
  };

  const fetchDataMember = async () => {
    try {
      const response = await api.get('/member/get-all-members');
      if (response.data && Array.isArray(response.data.data)) {
        const options = response.data.data.map(v => ({
          value: v._id,
          label: v.Name || 'Unknown',
          accno: v.Accno || ''
        }));
        setMemberOptions(options);
      }
    } catch (error) {
      console.error('Failed to fetch member data:', error);
    }
  };

  const fetchDataEvent = async () => {
    try {
      const response = await api.get('/event');
      if (response.data && Array.isArray(response.data.data)) {
        const options = response.data.data.map(v => ({
          value: v.eventName,
          label: v.eventName || 'Unknown'
        }));
        setEventOptions(options);
      }
    } catch (error) {
      console.error('Failed to fetch event data:', error);
    }
  };

  const handleAdd = async (values) => {
    try {
      const payload = {
        ...values,
        memberName: memberOptions.find(m => m.value === values.memberId)?.label || '',
        dateofpayment: values.dateofpayment ? moment(values.dateofpayment).format('YYYY-MM-DD') : 'N/A',
        eventDate: values.eventDate ? moment(values.eventDate).format('YYYY-MM-DD') : null,
      };
      const response = await api.post('/transaction/addTransaction', payload);
      if (response.status === 200 && response.data.success) {
        setData(prevData => [...prevData, response.data.data]);
        message.success('Transaction added successfully');
        setIsModalVisible(false);
        form.resetFields();
      }
    } catch (error) {
      message.error('Failed to add transaction.');
    }
  };

  const handleUpdate = async (values) => {
    try {
      if (!currentRecord || !currentRecord._id) return;
      const payload = {
        ...values,
        memberName: memberOptions.find(m => m.value === values.memberId)?.label || '',
        dateofpayment: values.dateofpayment ? moment(values.dateofpayment).format('YYYY-MM-DD') : 'N/A',
        eventDate: values.eventDate ? moment(values.eventDate).format('YYYY-MM-DD') : null,
      };
      await api.put(`/transaction/updateTransaction/${currentRecord._id}`, payload);
      fetchData();
      message.success('Transaction updated successfully');
      setIsModalVisible(false);
      form.resetFields();
    } catch (error) {
      message.error('Failed to update transaction.');
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/transaction/deleteTransaction/${id}`);
      fetchData();
      message.success('Transaction deleted successfully');
    } catch (error) {
      message.error('Failed to delete transaction.');
    }
  };

  const handleEdit = (record) => {
    setEditMode(true);
    setCurrentRecord(record);
  
    // Find the memberId from memberOptions based on memberName
    const selectedMember = memberOptions.find((m) => m.label === record.memberName) || {};
  
    form.setFieldsValue({
      memberId: selectedMember.value || '', // Set the memberId for the dropdown
      Accno: record.Accno || '',
      memberName: record.memberName || '',
      eventName: record.eventName || '',
      donation: record.donation || '',
      donateProd: record.donateProd || '',
      phone: record.phone || '',
      address: record.address || '',
      eventDate: record.createdAt ? moment(record.createdAt) : null,
      paymentmode: record.paymentmode || '',
      dateofpayment: record.dateofpayment ? moment(record.dateofpayment) : null,
      remarks: record.remarks || '',
    });
    setIsModalVisible(true);
  };
  const exportExcel = () => {
    const excelData = filteredData.map((record, index) => {
      const member = memberOptions.find((m) => m.value === record.memberId?._id) || {};
      return {
        "S.No": index + 1,
        "ACCI Code": record.Accno || "N/A",
        "Event Name": record.eventName || "N/A",
        "Member Name": member.label || record.memberName || "N/A",
        "Donation Type": record.donation || "N/A",
        "Amount/Product": record.donateProd || "N/A",
        "Payment Mode": record.paymentmode || "N/A",
        "Date of Payment": record.dateofpayment ? moment(record.dateofpayment).format('YYYY-MM-DD') : "N/A",
        "Event Date": record.createdAt ? moment(record.createdAt).format('YYYY-MM-DD') : "N/A",
        "Remarks": record.remarks || "N/A",
      };
    });
  
    const totalRow = {
      "S.No": "",
      "ACCI Code": "",
      "Event Name": "",
      "Member Name": "Total",
      "Donation Type": "",
      "Amount/Product": filteredData.reduce((sum, record) => sum + (parseFloat(record.donateProd) || 0), 0).toFixed(2),
      "Payment Mode": "",
      "Date of Payment": "",
      "Event Date": "",
      "Remarks": "",
    };
  
    const worksheet = XLSX.utils.json_to_sheet([...excelData, totalRow]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Transactions");
    XLSX.writeFile(workbook, "transactions.xlsx");
  };

  const exportToPDF = () => {
    const doc = new jsPDF({ format: 'a5', unit: 'mm' });
    const pageWidth = doc.internal.pageSize.getWidth();

    doc.addImage(SiteLogo, 'PNG', 10, 5, 30, 20);
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Transactions Report", pageWidth / 2, 15, { align: "center" });

    doc.autoTable({
      startY: 30,
      head: [['S.No', 'ACCI Code', 'Member', 'Event', 'Event Date', 'Donation', 'Amount', 'Payment Mode']],
      body: filteredData.map((item, index) => [
        index + 1,
        item.Accno || 'Unknown',
        item.memberName,
        item.eventName,
        moment(item.createdAt).format('YYYY-MM-DD'),
        item.donation,
        item.donateProd,
        item.paymentmode || 'N/A',
      ]),
      styles: { fontSize: 8 },
      margin: { left: 5, right: 5 },
      tableWidth: 'auto'
    });

    doc.save("Transactions.pdf");
  };

  const handlePrintSingleTransactionPDF = (record) => {
    const doc = new jsPDF({ unit: 'mm', format: [72, 297] });
    const pageWidth = doc.internal.pageSize.getWidth();

    doc.addImage(SiteLogo, 'PNG', (pageWidth - 30) / 2, 5, 30, 20);
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("Transaction Receipt", pageWidth / 2, 30, { align: "center" });

    const details = [
      ['ACCI Code', record.Accno || 'N/A'],
      ['Member Name', record.memberName || 'N/A'],
      ['Event', record.eventName || 'N/A'],
      ['Date', record.dateofpayment ? moment(record.dateofpayment).format('YYYY-MM-DD') : 'N/A'],
      ['Donation', record.donation || 'N/A'],
      ['Amount / Product', record.donateProd || 'N/A'],
      ['Payment Mode', record.paymentmode || 'N/A'],
      ['Remarks', record.remarks || 'N/A'],
    ];

    doc.autoTable({
      startY: 40,
      head: [['Field', 'Value']],
      body: details,
      styles: { fontSize: 8 },
      margin: { left: 5, right: 5 },
      tableWidth: pageWidth - 10,
    });

    const pdfBlob = doc.output('blob');
    const pdfURL = URL.createObjectURL(pdfBlob);
    window.open(pdfURL, '_blank');
  };

  const filteredData = data.filter(item => {
    const matchesEvent = selectedFilterEvent ? item.eventName === selectedFilterEvent : true;
    const matchesSearch = searchText
      ? [
          item.Accno,
          item.memberName,
          item.eventName,
          item.donation,
          item.donateProd,
          item.paymentmode,
          item.remarks
        ].some(field => 
          field && field.toString().toLowerCase().includes(searchText.toLowerCase())
        )
      : true;
    return matchesEvent && matchesSearch;
  });

  const columns = [
    {
      title: 'S.No',
      key: 'sno',
      render: (text, record, index) => (pagination.current - 1) * pagination.pageSize + index + 1,
      width: '5%',
    },
    { title: 'ACCI Code', dataIndex: 'Accno', key: 'Accno', width: '10%' },
    { title: 'Member Name', dataIndex: 'memberName', key: 'memberName', width: '15%' },
    { title: 'Event Name', dataIndex: 'eventName', key: 'eventName', width: '15%' },
    {
      title: 'Event Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: '10%',
      render: (text) => text ? moment(text).format('YYYY-MM-DD') : 'N/A',
    },
    { title: 'Donation', dataIndex: 'donation', key: 'donation', width: '10%' },
    { title: 'Amount', dataIndex: 'donateProd', key: 'donateProd', width: '10%' },
    { title: 'Payment Mode', dataIndex: 'paymentmode', key: 'paymentmode', width: '10%' },
    {
      title: 'Date of Payment',
      dataIndex: 'dateofpayment',
      key: 'dateofpayment',
      width: '10%',
      render: (text) => text ? moment(text).format('YYYY-MM-DD') : 'N/A',
    },
    {
      title: 'Remarks',
      dataIndex: 'remarks',
      key: 'remarks',
      width: '12%',
      render: (text) => text || 'N/A',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          <Button icon={<DeleteOutlined />} danger onClick={() => handleDelete(record._id)} />
          <Button icon={<PrinterOutlined />} onClick={() => handlePrintSingleTransactionPDF(record)} />
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '20px' }}>
      <h2><RiFolderTransferFill /> Transactions</h2>
      <Row gutter={16} style={{ marginBottom: '20px' }}>
        <Col span={10}>
          <Input
            placeholder="Search transactions"
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: '50%' }}
          />
          <Button style={{ marginLeft: 10 }} onClick={exportToPDF}>Export PDF</Button>
          <Button style={{ marginLeft: 10 }} onClick={exportExcel}>Export Excel</Button>
        </Col>
        <Col span={8}>
          <Select
            placeholder="Filter by Event Name"
            allowClear
            style={{ width: '100%' }}
            onChange={(value) => setSelectedFilterEvent(value || null)}
            options={eventOptions}
          />
        </Col>
        <Col span={6} style={{ textAlign: 'right' }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => { setIsModalVisible(true); setEditMode(false); form.resetFields(); }}
          >
            Add Transaction
          </Button>
        </Col>
      </Row>
      <Table
        columns={columns}
        dataSource={filteredData}
        rowKey="_id"
        pagination={pagination}
        loading={loading}
        onChange={(newPagination) => setPagination(newPagination)}
      />
      <Modal
        title={editMode ? 'Edit Transaction' : 'Add New Transaction'}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onOk={() => form.submit()}
        width={800}
      >
        <Form form={form} onFinish={editMode ? handleUpdate : handleAdd} layout="vertical">
          {/* Row 1 */}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="eventName" label="Event" rules={[{ required: true }]}>
                <Select>
                  {eventOptions.map(opt => (
                    <Select.Option key={opt.value} value={opt.value}>{opt.label}</Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="memberId" label="Member" rules={[{ required: true }]}>
                <Select>
                  {memberOptions.map(opt => (
                    <Select.Option key={opt.value} value={opt.value}>{opt.label}</Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {/* Row 2 */}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="donation" label="Donation" rules={[{ required: true }]}>
                <Radio.Group>
                  <Radio value="Amount">Amount</Radio>
                  <Radio value="Product">Product</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="donateProd" label="Product/Amount">
                <Input />
              </Form.Item>
            </Col>
          </Row>

          {/* Row 3 */}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="paymentmode" label="Payment Mode" rules={[{ required: true }]}>
                <Select>
                  <Select.Option value="Cash">Cash</Select.Option>
                  <Select.Option value="UPI">UPI</Select.Option>
                  <Select.Option value="Bank">Bank</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="dateofpayment" label="Date of Payment" rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
              </Form.Item>
            </Col>
          </Row>

          {/* Row 4 */}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="phone" label="Phone">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="address" label="Address">
                <Input />
              </Form.Item>
            </Col>
          </Row>

          {/* Row 5 */}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="eventDate" label="Event Date" rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="remarks" label="Remarks">
                <Input />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default Transactions;
